import { useRef } from 'react';
import Container from '@/components/feature/Container';
import ScrollExpandPhoto from '@/components/feature/ScrollExpandPhoto';
import Reveal from '@/components/base/Reveal';
import Eyebrow from '@/components/base/Eyebrow';
import Photo from '@/components/base/Photo';
import PillLink from '@/components/base/PillLink';
import { lifeGallery, livingSpaces } from '@/mocks/life';

export default function LifeHere() {
  const [g1, g2, g3, g4] = lifeGallery;
  const boxRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <section className="pt-10 md:pt-16">
        <Container>
          <div ref={boxRef} className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <Eyebrow>Life Here</Eyebrow>
              <h1 className="mt-5 text-[42px] sm:text-[56px] lg:text-[76px] leading-[1.02] font-medium tracking-[-0.02em]">A life that&rsquo;s <span className="em-serif">yours.</span></h1>
            </div>
            <p className="text-[16px] md:text-[18px] leading-relaxed text-foreground-700 max-w-sm">Your routines, your friendships, your interests &mdash; carried on, with a little support whenever it&rsquo;s wanted.</p>
          </div>
        </Container>
        <ScrollExpandPhoto
          alignRef={boxRef}
          alt="Group of older friends laughing together on a sunny garden lawn"
          src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-lh-hero.webp"
        />
      </section>

      <section className="py-16 md:py-28">
        <Container>
          <Reveal>
            <Eyebrow>Everyday Moments</Eyebrow>
            <h2 className="mt-5 text-[32px] md:text-[46px] leading-[1.08] font-medium tracking-[-0.02em]">Days filled <span className="em-serif">your way.</span></h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
            {[{ item: g1, cls: 'md:col-span-7', h: 'h-[300px] md:h-[480px]' },
              { item: g2, cls: 'md:col-span-5', h: 'h-[360px] md:h-[480px]' },
              { item: g3, cls: 'md:col-span-5', h: 'h-[360px] md:h-[480px]' },
              { item: g4, cls: 'md:col-span-7', h: 'h-[300px] md:h-[480px]' }].map(({ item, cls, h }, i) => (
              <Reveal key={item.cap} className={cls} delay={(i % 2) * 100}>
                <div className="relative">
                  <Photo className={`w-full ${h} rounded-2xl`} reveal="down" revealGroup="life-moments" revealVariant="portrait" src={item.img} alt={item.alt} />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 rounded-b-2xl bg-gradient-to-t from-foreground-950/55 via-foreground-950/15 to-transparent px-5 pb-4 pt-14 md:px-6 md:pb-5">
                    <p className="text-[15px] md:text-base font-medium text-background-50">{item.cap}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-accent-500 text-background-50">
        <Container className="py-20 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end">
          <Reveal className="lg:col-span-7">
            <h2 className="text-[34px] md:text-[56px] leading-[1.06] font-medium tracking-[-0.02em]">Good days are made of <span className="em-serif">small things.</span></h2>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={100}>
            <p className="text-[16px] md:text-[18px] leading-relaxed text-background-50/90">A lie-in when you fancy one. A favourite song in the afternoon. A friend saving you a seat at dinner. We pay attention to choice, comfort and friendship &mdash; the everyday details that quietly add up to happiness.</p>
          </Reveal>
        </Container>
      </section>

      <section className="py-16 md:py-28 bg-background-100">
        <Container>
          <Reveal>
            <Eyebrow>Living Spaces</Eyebrow>
            <h2 className="mt-5 text-[32px] md:text-[46px] leading-[1.08] font-medium tracking-[-0.02em]">Familiar, comfortable, <span className="em-serif">lived-in.</span></h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-5">
            {livingSpaces.map((s, i) => (
              <Reveal key={s.t} delay={i * 100}>
                <Photo className="w-full h-[420px] md:h-[520px] rounded-2xl" reveal="down" revealGroup="life-spaces" revealVariant="portrait" src={s.img} alt={s.alt} />
                <h3 className="mt-5 text-[21px] md:text-[23px] font-medium">{s.t}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-foreground-700">{s.d}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-28">
        <Container>
          <Reveal>
            <Photo
              className="w-full aspect-[4/3] md:aspect-[16/7] rounded-2xl"
              position="object-[center_40%]"
              reveal="right"
              revealVariant="wide"
              alt="Two older men laughing together over coffee on a terrace"
              src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-lh-comm.webp"
            />
          </Reveal>
          <Reveal className="mt-10 md:mt-16 max-w-[900px]" delay={100}>
            <Eyebrow>Community & Connection</Eyebrow>
            <h2 className="mt-5 text-[36px] sm:text-[48px] md:text-[64px] lg:text-[76px] leading-[1.02] font-medium tracking-[-0.025em]">Belonging makes a <span className="em-serif">difference.</span></h2>
            <p className="mt-8 md:mt-10 max-w-[580px] text-[16px] md:text-[17px] leading-relaxed text-foreground-700">Friendships grow naturally here &mdash; over breakfast, in the garden, during a shared hobby. Families drop in, neighbours become friends, and the team know each person by more than their name.</p>
            <p className="mt-4 max-w-[580px] text-[16px] md:text-[17px] leading-relaxed text-foreground-700">It&rsquo;s the companionship of ordinary days that so often makes the biggest difference.</p>
          </Reveal>
        </Container>
      </section>

      <section className="relative h-[520px] md:h-[680px]" data-dw-reveal="right" data-dw-reveal-variant="wide" data-dw-reveal-layer="under">
        <img
          loading="lazy"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-center"
          alt="Older woman and her granddaughter walking through a sunlit garden"
          title="Visit Daywell Living"
          src="https://public.readdy.ai/ai/img_res/sandbox/d9f57693db1a3f647bcb80a40a4edde0/5fa7b452-069c-4ae4-8f43-36e8851e8c88_dw-lh-cta.webp"
        />
        <div className="absolute inset-0 bg-black/40" />
        <Container className="relative h-full flex flex-col justify-end pb-14 md:pb-20 text-background-50">
          <h2 className="text-[34px] md:text-[58px] leading-[1.05] font-medium tracking-[-0.02em] lg:whitespace-nowrap">Come see what life here <span className="em-serif">feels like.</span></h2>
          <div className="mt-8"><PillLink to="/visit#enquiry" variant="light">Arrange a Visit</PillLink></div>
        </Container>
      </section>
    </>
  );
}